import { useCallback, useMemo } from 'react';
import { alertError } from '../actions/sales/DocumentAction';
import { AlertType, IJobInfo, IJobProgress, Maybe, ReportOutputType } from '../graphql/schema/p4types';
import { IWorkspaceSelectionState } from '../components/Workspace/useWorkspaceSelection';
import { IJobMeta } from './BackgroundJobs';
import { IOnProcessSummary } from '../components/modals/DocumentModal2';
import { IDocumentValues } from '../components/document/documentCarouselUtil';
import { useApolloClient } from '@apollo/client';
import { UseFormWatch } from 'react-hook-form';
import { EntityType } from '../@types/entityTypes';
import { useP4ToastController } from '../views/system/NotificationsArea';
import * as Sentry from '@sentry/react';
import { useOnProcessPreview } from '../components/modals/EmailPrintPreviewModal';

export const onDocumentPrintFailed = (
  { showSummary }: { showSummary: boolean },
  { onProcessSummary }: { onProcessSummary: IOnProcessSummary },
  job?: IJobInfo | IJobProgress
) => {
  if (onProcessSummary) {
    onProcessSummary(false, { showSummary }, undefined, job, true);
  } else {
    console.warn('Job failed with no summary to process');
  }
};

export const onDocumentPrintComplete = (state: any, props: any, job: IJobInfo | IJobProgress) => {
  if (state.outputType === 'SYSTEM_PRINTER') {
    alertError('Documents', `Documents will be sent to the printer`, AlertType.Success, props.dispatch);
  } else if (state.outputType === 'EMAIL') {
    alertError('Documents', `Documents will be sent via email`, AlertType.Success, props.dispatch);
  } else if (state.outputType === 'EMAIL_WITH_PREVIEW') {
    props?.onProcessPreview(job as IJobMeta);
  } else {
    const reportWindow = window.open(`/api/resources/report/${job.properties.reportFile}`, '_blank');
    if (state.outputType === 'LOCAL_PRINTER') {
      reportWindow?.print();
    }
  }
  props.onClose();
  props.onComplete?.();
};

export type IDocumentSelectionState = IWorkspaceSelectionState & { include: boolean };

/**
 * Get the selection state of a workspace from the workspace ref.
 * Adds an 'include' flag (Inversion of selectedAll) because this will be passed directly through to queries.
 * (The original workspaceSelection does not require the extra flag).
 */
export function getSelectionState(ref: any): Maybe<IDocumentSelectionState> {
  if (!ref) {
    console.warn('Expected a workspace ref but none was provided.');
    return null;
  }

  // If you're having issues with this suddenly being null, and you've used useRef - try useCallBack instead
  const props = ref.props;

  const selectionState: IWorkspaceSelectionState = props.workspaceSelection.getSelectionState();
  return { ...selectionState, include: !selectionState.selectedAll };
}

type DocumentCompleteParameters = {
  overrideOutput?: boolean;
  outputType?: string;
  documentName: string;
  documentNamePlural?: string;
  dispatch: any;
  job: IJobInfo | IJobProgress | IJobMeta;
  onClose?: () => any;
  showSummary?: boolean;
  onProcessSummary?: () => any;
  onComplete?: () => any;
  onProcessPreview?: (job: IJobMeta) => void; //If the printed document contains any "EMAIL_PREVIEW" type prints, this is called to display the preview modal.
};

export const onDocumentComplete = ({
  overrideOutput,
  outputType,
  documentName,
  documentNamePlural,
  dispatch,
  job,
  onClose,
  showSummary,
  onProcessSummary,
  onComplete,
  onProcessPreview,
}: DocumentCompleteParameters) => {
  if (overrideOutput && outputType === ReportOutputType.SystemPrinter) {
    alertError(
      `${documentName} sent`,
      `${documentNamePlural || documentName} will be sent to the printer`,
      AlertType.Success,
      dispatch
    );
  } else if (overrideOutput && outputType === ReportOutputType.Email) {
    alertError(
      `${documentName} sent`,
      `${documentNamePlural || documentName} will be sent via email`,
      AlertType.Success,
      dispatch
    );
  } else if (overrideOutput && outputType === ReportOutputType.LocalPrinter) {
    alertError(
      `${documentName} sent`,
      `All ${documentName} will be sent to the local printer`,
      AlertType.Success,
      dispatch
    );
  }
  if (job.properties?.reportFile) {
    const reportWindow = window.open(`/api/resources/report/${job.properties.reportFile}`, '_blank');
    //TODO - Improved solution to printing in new window
    //     - This is the only way I can find to get it working for Firefox for the moment.
    if (outputType === 'LOCAL_PRINTER') {
      setTimeout(() => {
        try {
          reportWindow?.print();
        } catch (error) {
          Sentry.captureException(error);
        }
      }, 500);
    }
  }

  if (onClose) {
    onClose();
  }

  if (showSummary) {
    onProcessSummary?.();
  }

  if (onComplete) {
    onComplete();
  }

  onProcessPreview?.(job as IJobMeta);
};

/**
 * Note - Intended for use with DocumentCarousel component - Due to use of context to access values/methods, this should not be used outside of the DocumentCarousel.
 * @param watch - Watch method for the Document Carousel, to determine which behaviours to follow.
 * @param documentName - Name of this document (For toasts, i.e. 'Acknowledgement' for 'Acknowledgement sent').
 * @param documentName - Plural name of this document (For toasts).
 */
export const useDocumentCarouselComplete = ({
  watch,
  entity,
}: {
  watch: UseFormWatch<IDocumentValues>;
  entity: EntityType;
}) => {
  const { dispatchSimpleToast } = useP4ToastController();
  const onProcessPreview = useOnProcessPreview();

  return useCallback(
    (job: IJobMeta) => {
      const documentName = entity.title;
      const documentNamePlural = entity.configuration?.title ?? `${entity.title}s`;

      const overrideOutput = watch('overrideOutput');
      const outputType = watch('outputType');
      const showSummary = watch('showSummary');

      if (overrideOutput && outputType === ReportOutputType.SystemPrinter) {
        dispatchSimpleToast(
          `${documentName} sent`,
          `${documentNamePlural || documentName} will be sent to the printer`,
          AlertType.Success
        );
      } else if (overrideOutput && outputType === ReportOutputType.Email) {
        dispatchSimpleToast(
          `${documentName} sent`,
          `${documentNamePlural || documentName} will be sent via email`,
          AlertType.Success
        );
      } else if (overrideOutput && outputType === ReportOutputType.LocalPrinter) {
        dispatchSimpleToast(
          `${documentName} sent`,
          `All ${documentName} will be sent to the local printer`,
          AlertType.Success
        );
      }
      if (job.properties?.reportFile) {
        const reportWindow = window.open(`/api/resources/report/${job.properties.reportFile}`, '_blank');
        //TODO - Improved solution to printing in new window
        //     - This is the only way I can find to get it working for Firefox for the moment.
        if (outputType === 'LOCAL_PRINTER') {
          setTimeout(() => {
            reportWindow?.print();
          }, 500);
        }
      }

      if (showSummary) {
        window.open(`/Company/DocumentSummary/Workspace/${job!.jobId}`, '_blank');
      }

      //Trigger email preview modal (if appropriate).
      onProcessPreview(job);
    },
    [dispatchSimpleToast, entity.configuration?.title, entity.title, watch]
  );
};

/**
 * Helper function to manage job progress and cache updates when a document printing job is completed.
 * @param evictionFieldNames - Field names to evict from the Apollo Cache on completion.
 *                           - For example 'salesOrder' to cause a concurrency reload on the SO Form, or 'acknowledgements' to refetch workspace data.
 * @param onDocumentComplete - Document completion logic as configured by the useDocumentCarouselDefaults function.
 * @param onPrintComplete - Optionally provided callback (To the printDocument functions) to run after printing is completed.
 */
export const useDocumentJobCompletion = ({
  evictionFieldNames,
  onDocumentComplete,
  onPrintComplete,
}: {
  evictionFieldNames: Array<string>;
  onDocumentComplete: ReturnType<typeof useDocumentCarouselComplete>;
  onPrintComplete?: (job: IJobMeta) => void;
}): [functions: { onJobComplete: (job: IJobMeta) => void; onJobFailed: () => void }, data: { inProgress: boolean }] => {
  const apolloClient = useApolloClient();
  return useCallback(
    (job: IJobMeta) => {
      onDocumentComplete(job);
      //Evict related records from the apollo cache, forcing a refetch.
      // - If we're on a form (I.e. SalesOrderForm) this should initial a concurrency reload
      evictionFieldNames?.forEach((fieldName) => {
        apolloClient.cache.evict({ fieldName });
      });
      apolloClient.cache.gc();
      onPrintComplete?.(job);
    },
    [apolloClient.cache, evictionFieldNames, onDocumentComplete, onPrintComplete]
  );
};
